import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { Clock, Users, Euro, AlertCircle } from 'lucide-react';
import CalendarService from '../../services/CalendarService';
import { CalendarSlot } from '../../types/calendar.types';
import { calculateTieredPrice, TourTypeWithPriceTiers } from '../../utils/pricingUtils';
import { API_BASE_URL } from '../../config/api.config';
import '../../styles/components/TimeSlotPicker.css';

interface TimeSlotPickerProps {
  date: Date;
  tourType?: string;
  participants: number;
  onSelectSlot: (slot: CalendarSlot) => void;
  isAdminBooking?: boolean; // Admin kann Kapazitätsgrenzen umgehen
}

const TimeSlotPicker: React.FC<TimeSlotPickerProps> = ({
  date,
  tourType,
  participants,
  onSelectSlot,
  isAdminBooking = false,
}) => {
  const [slots, setSlots] = useState<CalendarSlot[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedSlotId, setSelectedSlotId] = useState<number | null>(null);
  const [tourTypes, setTourTypes] = useState<TourTypeWithPriceTiers[]>([]);

  // Load tour types with price tiers on mount
  useEffect(() => {
    const loadTourTypes = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/tour-types`);
        if (response.ok) {
          const data = await response.json();
          console.log('[TimeSlotPicker] Loaded tour types with price tiers:', data.length);
          setTourTypes(data);
        }
      } catch (error) {
        console.error('[TimeSlotPicker] Error loading tour types:', error);
      }
    };
    loadTourTypes();
  }, []);

  useEffect(() => {
    loadSlotsForDate();
  }, [date, tourType, participants]);

  const loadSlotsForDate = async () => {
    setLoading(true);
    setError(null);

    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      console.log('[TimeSlotPicker] Loading slots for date:', dateStr, 'tourType:', tourType, 'participants:', participants);

      const availableSlots = await CalendarService.getSlotsForDate(dateStr);
      console.log('[TimeSlotPicker] API returned slots:', availableSlots);
      console.log('[TimeSlotPicker] Number of slots from API:', availableSlots?.length || 0);

      // Filter by tour type if specified
      let filteredSlots = availableSlots;
      if (tourType) {
        console.log('[TimeSlotPicker] ===== FILTERING START =====');
        console.log('[TimeSlotPicker] Filtering by tourType:', tourType);
        console.log('[TimeSlotPicker] Total slots before filter:', availableSlots.length);

        // Normalize function: converts any format to normalized string
        const normalize = (str: string) => {
          const result = str
            .toUpperCase()
            .replace(/[_\s-]+/g, '_')  // Replace spaces, hyphens, underscores with single underscore
            .replace(/[^A-Z0-9_]/g, '');  // Remove any other special chars
          console.log('[TimeSlotPicker] normalize("' + str + '") => "' + result + '"');
          return result;
        };

        const selectedTypeNormalized = normalize(tourType);
        console.log('[TimeSlotPicker] Selected type normalized:', selectedTypeNormalized);

        // BUG #32 FIX: FixedPrice tour types (PRIVAT, HOCHZEIT, EXKLUSIV) should show ALL slots
        // These tour types are for exclusive bookings where the admin can choose any slot
        // and the entire boat is blocked, regardless of the original slot tour type.
        const fixedPriceTourTypes = ['PRIVAT', 'HOCHZEIT', 'EXKLUSIV', 'EXCLUSIVE'];
        const isFixedPriceTourType = fixedPriceTourTypes.some(fp =>
          selectedTypeNormalized.includes(fp) || selectedTypeNormalized.startsWith(fp)
        );

        if (isFixedPriceTourType) {
          console.log('[TimeSlotPicker] FixedPrice tour type detected - showing ALL slots for admin to choose from');
          filteredSlots = availableSlots; // Don't filter - show all slots for exclusive bookings
        } else {
          filteredSlots = availableSlots.filter(slot => {
            const slotTypeNormalized = normalize(slot.tourType || '');
            const matches = slotTypeNormalized === selectedTypeNormalized;

            console.log('[TimeSlotPicker] Slot', slot.id,
              'tourType:', slot.tourType,
              '-> normalized:', slotTypeNormalized,
              'vs selected:', selectedTypeNormalized,
              'matches:', matches);

            return matches;
          });
        }
        console.log('[TimeSlotPicker] ===== FILTERING END =====');
        console.log('[TimeSlotPicker] Slots after tour type filter:', filteredSlots.length);
      } else {
        console.log('[TimeSlotPicker] NO FILTER - tourType is', tourType);
      }

      // Sort by start time
      filteredSlots.sort((a, b) => {
        const timeA = (a.time || a.startTime || '00:00').split(':').map(Number);
        const timeB = (b.time || b.startTime || '00:00').split(':').map(Number);
        return (timeA[0] * 60 + timeA[1]) - (timeB[0] * 60 + timeB[1]);
      });

      console.log('[TimeSlotPicker] Final filtered and sorted slots:', filteredSlots);
      console.log('[TimeSlotPicker] Setting slots state with', filteredSlots.length, 'slots');
      setSlots(filteredSlots);
    } catch (err) {
      console.error('[TimeSlotPicker] Error loading time slots:', err);
      setError('Fehler beim Laden der verfügbaren Zeiten');
    } finally {
      setLoading(false);
    }
  };

  const getAvailabilityPercentage = (slot: CalendarSlot): number => {
    const maxCap = slot.maxCapacity || slot.maxParticipants || 0;
    const currentBookings = slot.currentBookings || slot.currentParticipants || 0;
    const available = maxCap - currentBookings;
    return maxCap > 0 ? (available / maxCap) * 100 : 0;
  };

  const getAvailabilityColor = (slot: CalendarSlot): string => {
    const percentage = getAvailabilityPercentage(slot);
    if (percentage > 50) return 'availability-good';
    if (percentage > 20) return 'availability-medium';
    return 'availability-low';
  };

  const isSlotAvailable = (slot: CalendarSlot): boolean => {
    // Admin kann immer buchen (Überbuchungen erlaubt)
    if (isAdminBooking) {
      return true;
    }

    const maxCap = slot.maxCapacity || slot.maxParticipants || 0;
    const currentBookings = slot.currentBookings || slot.currentParticipants || 0;
    const available = maxCap - currentBookings;
    return available >= participants;
  };

  const needsMoreParticipants = (slot: CalendarSlot): number => {
    const currentBookings = slot.currentBookings || slot.currentParticipants || 0;
    const minCap = slot.minCapacity || slot.minParticipants || 0;
    const currentTotal = currentBookings + participants;
    if (currentTotal >= minCap) return 0;
    return minCap - currentTotal;
  };

  const handleSlotSelection = (slot: CalendarSlot) => {
    if (!isSlotAvailable(slot)) return;
    
    setSelectedSlotId(slot.id);
    onSelectSlot(slot);
  };

  if (loading) {
    return (
      <div className="time-slot-picker">
        <div className="loading-spinner">
          <Clock className="animate-spin" size={48} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="time-slot-picker">
        <div className="error-message">
          <AlertCircle />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  if (slots.length === 0) {
    return (
      <div className="time-slot-picker">
        <div className="no-slots-message">
          <AlertCircle />
          <span>Keine verfügbaren Zeiten für dieses Datum</span>
        </div>
      </div>
    );
  }

  return (
    <div className="time-slot-picker">
      <div className="date-header">
        <h3>Verfügbare Zeiten am {format(date, 'EEEE, d. MMMM yyyy', { locale: de })}</h3>
      </div>

      <div className="time-slots-grid">
        {slots.map(slot => {
          const maxCap = slot.maxCapacity || slot.maxParticipants || 0;
          const currentBookings = slot.currentBookings || slot.currentParticipants || 0;
          const available = maxCap - currentBookings;
          const isAvailable = isSlotAvailable(slot);
          const needsMore = needsMoreParticipants(slot);
          const isSelected = selectedSlotId === slot.id;

          return (
            <div
              key={slot.id}
              className={`time-slot ${isAvailable ? 'available' : 'unavailable'} ${isSelected ? 'selected' : ''} ${getAvailabilityColor(slot)}`}
              onClick={() => handleSlotSelection(slot)}
            >
              <div className="slot-header">
                <div className="slot-time">
                  <Clock size={16} />
                  <span>{slot.displayTime || slot.time || slot.startTime}</span>
                </div>
                <div className="slot-tour-type">
                  {slot.tourType}
                </div>
              </div>

              <div className="slot-body">
                <div className="slot-availability">
                  <Users size={16} />
                  <span>{available} von {maxCap} Plätzen verfügbar</span>
                </div>

                <div className="slot-capacity-bar">
                  <div 
                    className="capacity-fill"
                    style={{ width: `${maxCap > 0 ? (currentBookings / maxCap) * 100 : 0}%` }}
                  />
                </div>

                <div className="slot-price">
                  <Euro size={16} />
                  <span>
                    {(() => {
                      // Berechne den Staffelpreis basierend auf der Teilnehmerzahl
                      const priceResult = calculateTieredPrice(
                        slot.tourType,
                        participants,
                        tourTypes,
                        slot.pricePerPerson || slot.basePrice || 0
                      );
                      // Festpreis vs. Pro Person unterscheiden
                      if (priceResult.pricingMode === 'FixedPrice') {
                        return `${priceResult.pricePerPerson.toFixed(2)} € Festpreis`;
                      }
                      return `${priceResult.pricePerPerson.toFixed(2)} € pro Person`;
                    })()}
                  </span>
                </div>
              </div>

              {!isAvailable && (
                <div className="slot-unavailable-overlay">
                  Nicht genug Plätze verfügbar
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TimeSlotPicker;